import { DotLegalOverflowMenu, DotLegalPaper, DotLegalTable, ITableHeader } from "@dotlegal/dotlegal-ui-components";
import { Box, TableCell, TableRow, Typography } from "@mui/material";
import { useTranslation } from "../../localization/useTranslation";
import useUserReviewResponsibility from "./ReviewUserResponsibility.hooks";
import { UserResponsibilityCountTableModel } from "./ReviewUserResponsibility.type";
import ReviewUserResponsibilityDetailDialog from "./reviewUserResponsibilityDetailDialog/ReviewUserResponsibilityDetailDialog";
import TransferUserResponsibility from "../transferUserResponsibility/TransferUserResponsibility";
import { usePlanContext } from "../../auth/planProvider/PlanProvider";
import PurpleLockSvg from "../../common/svgs/purpleLock.svg?react";

export interface IReviewUserResponsibilityProps {
    userId: string;
}

function ReviewUserResponsibility(props: IReviewUserResponsibilityProps) {
    const { translateString } = useTranslation();
    const { platformFeatures } = usePlanContext();
    const { tableData, isLoading, refetch, selectedEntityType, setSelectedEntityType } = useUserReviewResponsibility(props);

    const headers: Array<ITableHeader<UserResponsibilityCountTableModel>> = [
        { property: "entityName", text: translateString("name") },
        { property: "count", text: translateString("count") },
    ];

    return (
        <>
            <Typography
                sx={(theme) => ({
                    color: theme.palette.primary.dark,
                    fontWeight: theme.typography.fontWeightBold,
                    fontSize: theme.typography.pxToRem(16),
                    marginTop: theme.spacing(3),
                    marginBottom: theme.spacing(1),
                })}
            >
                {translateString("reviewUserResponsibility")}
            </Typography>
            <DotLegalPaper>
                <DotLegalTable
                    headers={headers}
                    defaultOrderBy={"entityName"}
                    data={tableData}
                    isLoading={isLoading}
                    hidePagination
                    clickableRows
                    hasOverflowMenu
                    renderRow={(row, i) => (
                        <TableRow key={i} onClick={() => setSelectedEntityType(row.entityType)} sx={{ cursor: "pointer" }}>
                            <TableCell>{row.entityName}</TableCell>
                            <TableCell align="right">
                                <Box sx={(theme) => ({ fontWeight: theme.typography.fontWeightBold, color: theme.palette.primary.main })}>{row.count}</Box>
                            </TableCell>
                            <TableCell
                                align="right"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                }}
                            >
                                <DotLegalOverflowMenu
                                    items={[
                                        !platformFeatures.canTransferUserResponsibility
                                            ? {
                                                  menuItem: (
                                                      <Box sx={{ display: "flex" }}>
                                                          <span>{translateString("bulkAssign")}</span>
                                                          <Box sx={{ marginLeft: 0.5 }}>
                                                              <PurpleLockSvg />
                                                          </Box>
                                                      </Box>
                                                  ),
                                                  tooltip: translateString("upgradeToPaidPlanForAccessToTransferUserResponsibility"),
                                                  disabled: true,
                                                  tooltipPosition: "left",
                                              }
                                            : {
                                                  menuItem: translateString("bulkAssign"),
                                                  onClick: () => setSelectedEntityType(row.entityType),
                                              },
                                    ]}
                                />
                            </TableCell>
                        </TableRow>
                    )}
                />
                <Box sx={{ display: "flex", justifyContent: "flex-end", marginTop: 2 }}>
                    <TransferUserResponsibility fromUserId={props.userId} onTransferComplete={() => refetch()} buttonType="primary" />
                </Box>
            </DotLegalPaper>
            {selectedEntityType && (
                <ReviewUserResponsibilityDetailDialog
                    userId={props.userId}
                    entityType={selectedEntityType}
                    onDialogClose={() => setSelectedEntityType(undefined)}
                    onSaved={() => {
                        refetch();
                        setSelectedEntityType(undefined);
                    }}
                />
            )}
        </>
    );
}

export default ReviewUserResponsibility;
